import { useId, useState } from 'react';
import { Box } from '@mui/material';
import CheckRounded from '@mui/icons-material/CheckRounded';
import { useSelector } from 'react-redux';
import { PlayInput } from 'Types';
import { requestMidiConnectCheck } from 'Utils/playAlong/midiConnectCheck';
import { selectAccountPlayInput } from 'Utils/playAlong/effectiveGrading';
import {
  PLAY_INPUT_OPTIONS,
  PLAY_INPUT_QUESTION,
  SAVE_FAILED_MESSAGE,
  shownPlayInput,
} from 'Utils/playAlong/playInputAccount';
import { MUSEFLOW_ORANGE } from 'Components/Colors';
import { choiceOptionSx } from 'Components/ChoiceGroup/choiceStyles';
import { onRadioKeyDown, radioTabIndex } from 'Components/ChoiceGroup/radioGroupKeys';
import { usePlayInputChange } from './usePlayInputChange';
import PlayInputPicture from './PlayInputPicture';

// The three answers as tiles, the device screen's picture over the answer, side
// by side where the picker has more than 300px. With less (a phone-sized dialog)
// a tile would squeeze its title to a word a line, so the answers stack as rows:
// picture on the left, title beside it. The rows are the default, so a browser
// without container queries (iOS 15) gets a layout that fits any width.
const TILES_FROM_PX = 300.02;
const roomyPicker = `@container (min-width: ${TILES_FROM_PX}px)`;

const tileRowSx = {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '8px',
  [roomyPicker]: { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '10px' },
} as const;

const tileSx = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '12px',
  padding: '8px 40px 8px 12px',
  fontSize: '14px',
  lineHeight: 1.3,
  textAlign: 'left',
  [roomyPicker]: {
    flexDirection: 'column',
    gap: '10px',
    padding: '16px 10px 14px',
    textAlign: 'center',
  },
} as const;

const tilePictureSx = {
  height: '28px',
  width: '56px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  [roomyPicker]: { height: '40px', width: '78%' },
} as const;

// The chosen tile carries a check as well as its orange outline, so the answer
// does not rest on colour alone.
const chosenMarkSx = {
  position: 'absolute',
  top: 'calc(50% - 9px)',
  right: '12px',
  width: '18px',
  height: '18px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: MUSEFLOW_ORANGE,
  color: '#FFFFFF',
  [roomyPicker]: { top: '7px', right: '7px' },
} as const;

export interface PlayInputPickerProps {
  /** Called once a new answer is saved. */
  onSaved?: (playInput: PlayInput) => void;
}

/**
 * "What do you play on?": the one account answer, with its three choices. The
 * same control in Settings and in every play surface's menu.
 *
 * Choosing "A MIDI keyboard" saves it and then runs the keyboard connect
 * check, so a player with no keyboard connected is asked to connect one rather
 * than switched over silently. The other two choices just save.
 */
export default function PlayInputPicker({ onSaved }: PlayInputPickerProps) {
  const labelId = useId();
  const accountPlayInput = useSelector(selectAccountPlayInput);
  const changePlayInput = usePlayInputChange();
  const [saving, setSaving] = useState<PlayInput | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);
  const shown = shownPlayInput(accountPlayInput);
  const checkedIndex = PLAY_INPUT_OPTIONS.findIndex((option) => option.value === shown);
  // The keyboard moves focus without choosing (choosing saves, may ask for a
  // keyboard and closes a play surface's dialog); Space or Enter chooses. While
  // focus is in the group the Tab stop follows it; Tab out and back lands on the
  // checked answer again, as in any radio group.
  const [focusIndex, setFocusIndex] = useState<number | null>(null);

  const choose = async (next: PlayInput) => {
    if (saving !== null || next === shown) return;
    setSaving(next);
    setSaveFailed(false);
    const saved = await changePlayInput(next, 'settings');
    setSaving(null);
    if (!saved) {
      setSaveFailed(true);
      return;
    }
    if (next === 'midi') requestMidiConnectCheck();
    onSaved?.(next);
  };

  return (
    <Box data-testid="play-input-picker" sx={{ fontFamily: 'Lato', containerType: 'inline-size' }}>
      <Box
        id={labelId}
        sx={{
          fontSize: '16px',
          letterSpacing: '0.01em',
          color: '#ECECEC',
          marginBottom: '10px',
        }}
      >
        {PLAY_INPUT_QUESTION}
      </Box>
      <Box
        role="radiogroup"
        aria-labelledby={labelId}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setFocusIndex(null);
        }}
        sx={tileRowSx}
      >
        {PLAY_INPUT_OPTIONS.map((option, index) => {
          const selected = option.value === shown;
          return (
            <Box
              key={option.value}
              component="button"
              type="button"
              role="radio"
              aria-checked={selected}
              aria-disabled={saving !== null}
              tabIndex={radioTabIndex(index, focusIndex ?? checkedIndex)}
              onFocus={() => setFocusIndex(index)}
              onClick={() => void choose(option.value)}
              onKeyDown={onRadioKeyDown(index, PLAY_INPUT_OPTIONS.length, setFocusIndex, {
                followFocus: false,
                enabled: saving === null,
              })}
              sx={{
                ...choiceOptionSx(selected),
                ...tileSx,
                cursor: saving !== null ? 'progress' : 'pointer',
                opacity: saving !== null && saving !== option.value ? 0.6 : 1,
              }}
            >
              <Box component="span" sx={tilePictureSx}>
                <PlayInputPicture playInput={option.value} />
              </Box>
              <span>{option.title}</span>
              {selected && (
                <Box component="span" aria-hidden sx={chosenMarkSx}>
                  <CheckRounded sx={{ fontSize: '13px' }} />
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
      {saveFailed && (
        <Box role="alert" sx={{ marginTop: '10px', fontSize: '13px', color: '#F3C6B8' }}>
          {SAVE_FAILED_MESSAGE}
        </Box>
      )}
    </Box>
  );
}
